import classnames from 'classnames'
import BluebirdPromise from 'bluebird'
import { PureComponent, ReactNode } from 'react'
import { CloudUploadOutlined } from '@ant-design/icons'
import { Upload, Spin, message, UploadProps } from 'antd'
import { RcFile, UploadFile } from 'antd/lib/upload/interface'
import { UploadRequestOption } from 'rc-upload/lib/interface'

import Box from '~/core/Box'
import Button, { ButtonPropsType } from '~/core/Button'
import logger from '~/lib/logger'
import { isBlockedFileName } from '~/lib/fileExtensions'
import {
    UPLOAD_BLOCKED_ATTACHMENT_ERROR,
    UPLOAD_BLOCKED_SOME_ATTACHMENTS_ERROR,
} from '~/lib/flashMsgs'

import styles from './styles.css'

const BEFORE_UPLOAD_DELAY = 5000

const DONE = 'done'

type BeforeUpload = Required<UploadProps>['beforeUpload']

interface FileUploaderProps
    extends Pick<UploadProps, 'accept' | 'showUploadList' | 'disabled'>,
        Pick<ButtonPropsType, 'placement'> {
    dropzone?: boolean
    buttonSpin?: boolean
    className?: string
    centerSpinner?: boolean
    customSpinner?: ReactNode
    multiple?: boolean
    buttonType: ButtonPropsType['type']
    onError: (error: any) => void
    onBeforeUpload?: BeforeUpload
    onFileDropped: (file: RcFile, fileList: RcFile[]) => void
    onUpload: (
        file: UploadRequestOption['file'],
        options: UploadRequestOption,
    ) => Promise<any>
    onUploadSuccess: (
        originFileObj: RcFile | undefined,
        fileList: UploadFile[],
        file: UploadFile,
    ) => Promise<any>
    onSuccess: (fileList: UploadFile[]) => Promise<any>
    onChange?: (file: UploadFile, fileList: UploadFile[]) => void
    children?:
        | ReactNode
        | undefined
        | ((props: {
              isUploading: boolean
              spinner: ReactNode
              resetState: () => void
              filesCount: number | null
              completedCount: number
              hasCanceledUpload: boolean
          }) => ReactNode)
}

export default class FileUploader extends PureComponent<FileUploaderProps> {
    state = {
        isUploading: false,
    }

    filesCount: number | null = null

    completedCount = 0

    hasCanceledUpload = false

    reset = () => {
        this.setState({ isUploading: false })
        this.filesCount = null
        this.completedCount = 0
    }

    handleError = (error: any) => {
        logger.error(error)
        this.reset()

        if (typeof this.props.onError === 'function') {
            this.props.onError(error)
            return false
        }
        throw error
    }

    delayOrCallUpload = (file: RcFile, fileList: RcFile[]) => {
        return BluebirdPromise.delay(BEFORE_UPLOAD_DELAY).then(() => {
            return this.beforeUpload(file, fileList)
        })
    }

    beforeUpload: BeforeUpload = async (file, fileList) => {
        const blockedExtFileName = fileList
            .map(_file => (isBlockedFileName(_file.name) ? _file.name : ''))
            .filter(fileName => fileName)

        if (blockedExtFileName.length === fileList.length) {
            message.error(UPLOAD_BLOCKED_ATTACHMENT_ERROR)
            return false
        } else if (blockedExtFileName.length && fileList.length > 1) {
            message.warning(UPLOAD_BLOCKED_SOME_ATTACHMENTS_ERROR)
        }

        if (this.filesCount == null) {
            this.hasCanceledUpload = false
            this.filesCount = fileList.length
            this.setState({ isUploading: true })
        }

        // Keep calling delayOrCallUpload if
        // onBeforeUpload resolves to true
        if (
            this.props.onBeforeUpload &&
            (await this.props.onBeforeUpload(file, fileList))
        ) {
            return this.delayOrCallUpload(file, fileList)
        }

        // Create the entity in graphql
        const result = await Promise.resolve<ReturnType<BeforeUpload>>(
            this.props.onFileDropped(file, fileList),
        ).catch<boolean | never>(this.handleError)

        if (result === false) {
            this.hasCanceledUpload = true
            return false
        }

        return result
    }

    handleUpload: UploadProps['customRequest'] = opts => {
        const { file } = opts

        if (this.hasCanceledUpload) {
            return false
        }

        // Do the actual upload
        return Promise.resolve(this.props.onUpload(file, opts)).catch(
            this.handleError,
        )
    }

    onChange: UploadProps['onChange'] = ({ file, fileList }) => {
        const { onUploadSuccess, onSuccess, onChange } = this.props

        if (onChange !== undefined) {
            onChange(file, fileList)
        }

        if (this.hasCanceledUpload) {
            // We need to still keep count if the user canceled the uploads
            // so that we can reset after everything has been looped over
            // otherwise we might accidentally upload something that the
            // user wanted to abort
            this.completedCount += 1
            if (this.completedCount === this.filesCount) {
                this.reset()
            }
            return false
        } else if (file.status === DONE) {
            // Mark as uploaded
            this.completedCount += 1
            const promises = [
                onUploadSuccess(file.originFileObj, fileList, file),
            ]

            if (this.completedCount === this.filesCount) {
                if (typeof onSuccess === 'function') {
                    promises.push(onSuccess(fileList))
                }
                this.reset()
            }
            return Promise.all(promises)
        }

        return null
    }

    render() {
        const {
            dropzone: isDropzone = false,
            buttonSpin = false,
            children,
            accept,
            placement,
            className,
            showUploadList,
            buttonType,
            centerSpinner,
            customSpinner,
            multiple,
            disabled,
        } = this.props
        const { isUploading } = this.state

        let allowMultiple = true

        if (multiple !== undefined && !multiple) {
            allowMultiple = false
        }

        const spinner = centerSpinner ? (
            <Box alignItems="center" justifyContent="center">
                <Spin />
            </Box>
        ) : (
            customSpinner || <Spin />
        )

        let renderedChildren

        if (typeof children === 'function') {
            renderedChildren = children({
                isUploading,
                spinner,
                resetState: this.reset,
                filesCount: this.filesCount,
                completedCount: this.completedCount,
                hasCanceledUpload: this.hasCanceledUpload,
            })
        }

        // Note: Needs styles.dropzone b/c Upload.Dragger
        //       does not allow passing the style prop, and it
        //       wraps it as <span>
        if (isDropzone) {
            return (
                <Upload.Dragger
                    className={classnames(
                        isDropzone && styles.dropzone,
                        className,
                    )}
                    action=""
                    accept={accept}
                    beforeUpload={this.beforeUpload}
                    customRequest={this.handleUpload}
                    onChange={this.onChange}
                    showUploadList={showUploadList}
                    multiple={allowMultiple}
                    data-test-target="file-uploader"
                    disabled={disabled}
                >
                    {typeof children === 'function'
                        ? renderedChildren
                        : isUploading && !buttonSpin
                        ? spinner
                        : children || (
                              <Button
                                  name="select-files-for-upload"
                                  loading={isUploading}
                                  placement={placement}
                                  type={buttonType}
                                  data-test-target="select-or-drag-files"
                              >
                                  <CloudUploadOutlined /> Select
                                  {isDropzone ? ' or drag' : ''} files
                              </Button>
                          )}
                </Upload.Dragger>
            )
        } else {
            return (
                <Upload
                    className={classnames(
                        isDropzone && styles.dropzone,
                        className,
                    )}
                    action=""
                    accept={accept}
                    beforeUpload={this.beforeUpload}
                    customRequest={this.handleUpload}
                    onChange={this.onChange}
                    showUploadList={showUploadList}
                    multiple={allowMultiple}
                    data-test-target="file-uploader"
                    disabled={disabled}
                >
                    {typeof children === 'function'
                        ? renderedChildren
                        : isUploading && !buttonSpin
                        ? spinner
                        : children || (
                              <Button
                                  name="select-files-for-upload"
                                  loading={isUploading}
                                  placement={placement}
                                  type={buttonType}
                                  data-test-target="select-or-drag-files"
                              >
                                  <CloudUploadOutlined /> Select
                                  {isDropzone ? ' or drag' : ''} files
                              </Button>
                          )}
                </Upload>
            )
        }
    }
}
